<template>
  <main :class="doctorMode ? 'min-h-full bg-[#f5f7fa]' : 'min-h-screen bg-gray-50'">
    <div class="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:py-10">
      <router-link :to="doctorMode ? '/doctor/passes' : '/surveys'" class="text-sm font-medium text-[#667383] hover:text-[#0066ff]">← {{ doctorMode ? t('К прохождениям') : t('К списку опросов') }}</router-link>

      <div v-if="loading" class="mt-6 rounded-lg bg-white p-8 text-center text-gray-500">{{ t('Загрузка...') }}</div>
      <div v-else-if="error" class="mt-6 rounded-lg bg-red-50 p-8 text-center text-red-700">{{ error }}</div>

      <section v-else-if="submitted" class="mt-6 rounded-2xl border border-[#e1e7eb] bg-white p-6 shadow-sm sm:p-10">
        <span class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#e5efff] text-[#0066ff]"><svg class="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg></span>
        <h1 class="mt-4 text-center text-2xl font-semibold text-gray-900">{{ doctorMode ? t('Опрос завершён') : t('Спасибо') }}</h1>
        <p class="mt-1 text-center text-gray-600">{{ doctorMode ? t('Ответы и отметка сохранены в истории.') : t('Ваши ответы успешно отправлены.') }}</p>

        <!-- Блок результатов и пользовательской интерпретации -->
        <div v-if="userFacingResults.length" class="mx-auto mt-8 max-w-2xl text-left space-y-4">
          <div class="border-b border-gray-200 pb-2">
            <h2 class="text-lg font-semibold text-gray-900">{{ t('Ваш результат') }}</h2>
            <p class="text-xs text-gray-500">{{ t('Интерпретация результатов') }}</p>
          </div>

          <div class="space-y-4">
            <div
              v-for="item in userFacingResults"
              :key="item.scale_key"
              class="rounded-xl border border-gray-200 bg-[#fbfcfd] p-5 shadow-sm transition hover:shadow-md"
            >
              <div class="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
                <div class="flex items-center gap-2.5">
                  <span class="h-3 w-3 rounded-full flex-shrink-0" :style="{ backgroundColor: item.color || '#10B981' }"></span>
                  <h3 class="text-base font-semibold text-gray-800">{{ item.scale_label }}</h3>
                </div>
                <div class="flex items-center gap-2">
                  <span
                    class="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                    :style="{ backgroundColor: (item.color || '#10B981') + '20', color: item.color || '#10B981' }"
                  >
                    {{ item.level_label }}
                  </span>
                  <span class="rounded-lg bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-700">
                    {{ item.score }} / {{ item.max_score }}
                  </span>
                </div>
              </div>
              <p class="mt-3 text-sm leading-relaxed text-gray-700 whitespace-pre-line">
                {{ item.user_interpretation }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="result?.recommendation" class="mx-auto mt-6 max-w-2xl rounded-xl bg-green-50 p-4 text-left border border-green-200">
          <p class="font-semibold text-green-900">{{ result.recommendation.title }}</p>
          <p class="mt-1 text-sm text-green-800">{{ result.recommendation.message }}</p>
          <p v-if="recommendationDestination" class="mt-2 text-xs font-medium uppercase text-green-700">{{ recommendationDestination }}</p>
        </div>
        <div class="mt-7 flex flex-wrap justify-center gap-3">
          <router-link v-if="doctorMode && result?.response_id" :to="`/doctor/history/${result.response_id}`" class="inline-block rounded-xl bg-[#0066ff] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0056d6]">{{ t('Посмотреть результат') }}</router-link>
          <router-link :to="doctorMode ? '/doctor/passes' : '/surveys'" class="inline-block rounded-xl border border-[#dbe2e8] bg-white px-5 py-2.5 text-sm font-semibold text-[#52606e] hover:bg-[#f7f9fa]">{{ doctorMode ? t('К прохождениям') : t('Вернуться к опросам') }}</router-link>
        </div>
      </section>

      <form v-else-if="survey" class="mt-6 space-y-5" @submit.prevent="submit">
        <header class="rounded-2xl border border-[#e1e7eb] bg-white p-6 shadow-sm sm:p-8">
          <p v-if="doctorMode" class="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#0066ff]">{{ t('Прохождение опросника') }}</p>
          <h1 class="text-2xl font-semibold text-gray-900">{{ survey.name }}</h1>
          <p v-if="survey.description" class="mt-2 text-gray-600">{{ survey.description }}</p>
          <p v-if="survey.instructions" class="mt-4 rounded-md bg-blue-50 p-3 text-sm text-blue-900">{{ survey.instructions }}</p>
        </header>

        <section v-if="doctorMode" class="rounded-2xl border border-[#dfe7e3] bg-white p-6 shadow-sm sm:p-8">
          <div class="flex items-start gap-3">
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e5efff] text-[#0066ff]"><svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L8 18l-4 1 1-4z"/></svg></span>
            <div class="flex-1">
              <label for="survey-note" class="block text-base font-semibold text-[#27323e]">{{ t('Отметка') }}</label>
              <p class="mt-1 text-sm text-[#7c8793]">{{ t('Свободное поле, по которому вы сможете найти это прохождение в истории.') }}</p>
            </div>
          </div>
          <textarea id="survey-note" v-model="note" maxlength="500" rows="3" class="mt-5 w-full resize-y rounded-xl border border-[#d9e0e5] bg-[#fafbfc] px-4 py-3 text-sm text-[#2f3b47] outline-none transition placeholder:text-[#a1aab4] focus:border-[#0066ff] focus:bg-white focus:ring-2 focus:ring-[#0066ff]/10" :placeholder="t('Пример отметки')"></textarea>
          <div class="mt-1.5 text-right text-xs text-[#a0a9b3]">{{ note.length }}/500</div>
        </section>

        <article
          v-for="question in visibleQuestions"
          :key="question.id"
          class="rounded-2xl border border-[#e3e8ed] bg-white p-6 shadow-sm sm:p-8"
        >
          <div v-if="question.question_type === 'section_header'">
            <h2 class="text-xl font-semibold text-gray-900">{{ question.text }}</h2>
            <p v-if="question.description" class="mt-1 text-sm text-gray-500">{{ question.description }}</p>
          </div>

          <div v-else-if="question.question_type === 'info_block'">
            <p class="text-gray-800">{{ question.text }}</p>
            <p v-if="question.description" class="mt-1 text-sm text-gray-500">{{ question.description }}</p>
          </div>

          <div v-else>
            <label class="block text-base font-medium text-gray-900">
              {{ question.text }}
              <span v-if="question.is_required" class="text-red-500">*</span>
            </label>
            <p v-if="question.description" class="mt-1 text-sm text-gray-500">{{ question.description }}</p>

            <input
              v-if="question.question_type === 'short_text'"
              v-model="answers[question.id].text_value"
              class="mt-4 w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              type="text"
              :required="question.is_required"
            />

            <textarea
              v-else-if="question.question_type === 'long_text'"
              v-model="answers[question.id].text_value"
              class="mt-4 min-h-[120px] w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              :required="question.is_required"
            />

            <input
              v-else-if="question.question_type === 'date'"
              v-model="answers[question.id].text_value"
              class="mt-4 rounded-md border border-gray-300 px-3 py-2"
              type="date"
              :required="question.is_required"
            />

            <input
              v-else-if="question.question_type === 'time'"
              v-model="answers[question.id].text_value"
              class="mt-4 rounded-md border border-gray-300 px-3 py-2"
              type="time"
              :required="question.is_required"
            />

            <select
              v-else-if="question.question_type === 'dropdown'"
              v-model="answers[question.id].selected_options[0]"
              class="mt-4 w-full rounded-md border border-gray-300 px-3 py-2"
              :required="question.is_required"
            >
              <option value="">{{ t('Выберите вариант') }}</option>
              <option v-for="option in question.options" :key="option.id" :value="option.id">{{ option.text }}</option>
            </select>

            <div v-else-if="question.question_type === 'single_choice'" class="mt-4 space-y-3">
              <label v-for="option in question.options" :key="option.id" class="flex items-start gap-3">
                <input
                  v-model="answers[question.id].selected_options[0]"
                  type="radio"
                  :name="`q-${question.id}`"
                  :value="option.id"
                  :required="question.is_required"
                  class="mt-1"
                />
                <span>{{ option.text }}</span>
              </label>
            </div>

            <div v-else-if="question.question_type === 'multiple_choice'" class="mt-4 space-y-3">
              <label v-for="option in question.options" :key="option.id" class="flex items-start gap-3">
                <input
                  v-model="answers[question.id].selected_options"
                  type="checkbox"
                  :value="option.id"
                  class="mt-1"
                />
                <span>{{ option.text }}</span>
              </label>
            </div>

            <div v-else-if="isScale(question)" class="mt-4">
              <div class="mb-2 flex justify-between text-xs text-gray-500">
                <span>{{ question.settings?.min_label || t('Минимум') }}</span>
                <span>{{ question.settings?.max_label || t('Максимум') }}</span>
              </div>
              <div class="flex flex-wrap gap-2">
                <label
                  v-for="value in scaleValues(question)"
                  :key="value"
                  class="flex h-10 w-10 items-center justify-center rounded-full border text-sm"
                  :class="answers[question.id].numeric_value === value ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700'"
                >
                  <input
                    v-model.number="answers[question.id].numeric_value"
                    class="sr-only"
                    type="radio"
                    :name="`q-${question.id}`"
                    :value="value"
                    :required="question.is_required"
                  />
                  {{ value }}
                </label>
              </div>
            </div>

            <div v-else-if="question.question_type === 'matrix_radio'" class="mt-4 overflow-x-auto">
              <table class="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th class="border-b p-2 text-left"></th>
                    <th v-for="column in matrixColumns(question)" :key="column.value" class="border-b p-2 text-center">
                      {{ column.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in question.matrix_rows" :key="row.id">
                    <td class="border-b p-2">{{ row.text }}</td>
                    <td v-for="column in matrixColumns(question)" :key="column.value" class="border-b p-2 text-center">
                      <input
                        v-model.number="answers[question.id].matrix_values[row.id]"
                        type="radio"
                        :name="`q-${question.id}-row-${row.id}`"
                        :value="column.value"
                        :required="question.is_required"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else-if="question.question_type === 'matrix_checkbox'" class="mt-4 overflow-x-auto">
              <table class="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th class="border-b p-2 text-left"></th>
                    <th v-for="column in matrixColumns(question)" :key="column.value" class="border-b p-2 text-center">
                      {{ column.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in question.matrix_rows" :key="row.id">
                    <td class="border-b p-2">{{ row.text }}</td>
                    <td v-for="column in matrixColumns(question)" :key="column.value" class="border-b p-2 text-center">
                      <input
                        type="checkbox"
                        :checked="isMatrixChecked(question.id, row.id, column.value)"
                        @change="toggleMatrixCheckbox(question.id, row.id, column.value)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </article>

        <div class="flex justify-end">
          <button
            type="submit"
            :disabled="submitting"
            class="rounded-xl px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"
            :class="doctorMode ? 'bg-[#0066ff] hover:bg-[#0056d6]' : 'bg-blue-600 hover:bg-blue-700'"
          >
            {{ submitting ? t('Отправка...') : t('Отправить ответы') }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>

<script>
import api from '@/services/api'
import { t } from '@/i18n'

export default {
  name: 'SurveyTake',
  data() {
    return {
      survey: null,
      answers: {},
      loading: false,
      submitting: false,
      submitted: false,
      result: null,
      note: '',
      error: ''
    }
  },
  computed: {
    doctorMode() {
      return this.$route.matched.some(route => route.meta.doctorMode)
    },
    recommendationDestination() {
      const destination = this.result?.recommendation?.destination
      return {
        techniques: t('Рекомендуемый раздел: Техники'),
        safety_plan: t('Рекомендуемый раздел: План безопасности'),
        emergency: t('Рекомендуемый раздел: Тревожная кнопка')
      }[destination] || ''
    },
    visibleQuestions() {
      if (!this.survey) return []
      return this.survey.questions.filter(question => this.isQuestionVisible(question))
    },
    userFacingResults() {
      const results = this.result?.assessment?.user_results
      if (Array.isArray(results) && results.length) {
        return results.filter(item => Boolean(item.user_interpretation))
      }
      return []
    }
  },
  created() {
    this.loadSurvey()
  },
  methods: {
    t,
    async loadSurvey() {
      this.loading = true
      this.error = ''
      try {
        const response = await api.takeSurvey(this.$route.params.id)
        this.survey = response.data
        this.initializeAnswers()
      } catch (error) {
        this.error = error.response?.data?.detail || t('Не удалось загрузить опрос')
      } finally {
        this.loading = false
      }
    },
    initializeAnswers() {
      const answers = {}
      for (const question of this.survey.questions) {
        answers[question.id] = {
          question: question.id,
          text_value: '',
          numeric_value: null,
          selected_options: [],
          matrix_values: {}
        }
      }
      this.answers = answers
    },
    isScale(question) {
      return ['scale_1_5', 'scale_1_7', 'scale_1_10'].includes(question.question_type)
    },
    scaleValues(question) {
      const max = Number(question.question_type.split('_').pop())
      return Array.from({ length: max }, (_, index) => index + 1)
    },
    matrixColumns(question) {
      const settings = question.settings || {}
      const columns = settings.matrix_columns || settings.columns
      if (Array.isArray(columns) && columns.length) {
        return columns.map((column, index) => {
          if (typeof column === 'object') return column
          return { label: String(column), value: index }
        })
      }
      return [
        { label: '0', value: 0 },
        { label: '1', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 }
      ]
    },
    getAnswerDisplayValue(questionId) {
      const question = this.survey.questions.find(item => item.id === Number(questionId))
      const answer = this.answers[questionId]
      if (!question || !answer) return ''
      if (answer.text_value) return answer.text_value
      if (answer.numeric_value !== null && answer.numeric_value !== undefined) return String(answer.numeric_value)
      if (answer.selected_options.length) {
        return answer.selected_options.map(optionId => {
          const option = question.options.find(item => item.id === Number(optionId))
          return option?.text || String(optionId)
        }).join(', ')
      }
      return ''
    },
    isQuestionVisible(question) {
      const logic = question.settings?.conditional_logic
      if (!logic) return true
      const conditions = logic.conditions || (logic.source_question ? [logic] : [])
      if (!conditions.length) return true
      const results = conditions.map(condition => this.evaluateCondition(condition))
      return (logic.logic_operator || 'AND') === 'OR'
        ? results.some(Boolean)
        : results.every(Boolean)
    },
    evaluateCondition(condition) {
      const actual = this.getAnswerDisplayValue(condition.source_question)
      const expected = String(condition.value ?? '')
      if (condition.operator === 'not_equals') return actual !== expected
      if (condition.operator === 'contains') return actual.includes(expected)
      if (condition.operator === 'greater_than') return Number(actual) > Number(expected)
      if (condition.operator === 'less_than') return Number(actual) < Number(expected)
      return actual === expected
    },
    isMatrixChecked(questionId, rowId, value) {
      const rowValues = this.answers[questionId].matrix_values[rowId] || []
      return rowValues.includes(value)
    },
    toggleMatrixCheckbox(questionId, rowId, value) {
      const current = this.answers[questionId].matrix_values[rowId] || []
      this.answers[questionId].matrix_values[rowId] = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value]
    },
    buildPayload() {
      return this.visibleQuestions
        .filter(question => !['section_header', 'info_block'].includes(question.question_type))
        .map(question => {
          const answer = this.answers[question.id]
          return {
            question: question.id,
            text_value: answer.text_value || null,
            numeric_value: answer.numeric_value,
            selected_options: answer.selected_options.filter(Boolean),
            matrix_values: Object.keys(answer.matrix_values).length ? answer.matrix_values : null
          }
        })
    },
    async submit() {
      this.submitting = true
      this.error = ''
      try {
        const lang = this.survey?.language || localStorage.getItem('arqau_lang') || 'ru'
        const response = await api.submitSurvey(
          this.survey.id,
          this.buildPayload(),
          this.doctorMode ? this.note : '',
          lang
        )
        this.result = response.data
        this.submitted = true
      } catch (error) {
        this.error = error.response?.data?.note?.[0] || error.response?.data?.note || error.response?.data?.detail || t('Не удалось отправить ответы')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
